import { serverErrorMessage } from '@/services/httpService'
import { useClubClaimsExport, useDownloadClubClaimsCsv } from '@/services/clubBenefits'
import { useEffect, useRef, useState } from 'react'
import { CMButton } from './ui'

/** How long to leave between polls. Long enough not to hammer, short enough to feel answered. */
const POLL_INTERVAL_MS = 2000
/** Give up after this long. A run that has not finished by now has almost certainly failed. */
const POLL_TIMEOUT_MS = 5 * 60 * 1000

type Phase = { state: 'idle' } | { state: 'working'; message: string } | { state: 'failed'; message: string }

/**
 * Getting the claim list out of the app.
 *
 * Two buttons because the server offers two genuinely different things, and which one a club wants
 * depends on how much history it has:
 *
 * - **Download CSV** streams the file on the spot. One click, no waiting on anything — but the
 *   connection stays open for as long as the file takes to build, so a club with years of claims
 *   can lose it to a load-balancer timeout before it arrives.
 * - **Build in the background** hands the work to a job and polls for it. Slower for a short list
 *   because of the round trip, and the only thing that works for a long one.
 *
 * Labelled by what each does rather than by which is "advanced", so a director picks on the fact
 * they know — how long their list is — rather than on a word they would have to interpret.
 *
 * Names in both files are initialled. That is the server's decision, said here because a director
 * reading the file needs to know why it does not match the screen.
 */
export const ClubClaimsExport = ({ clubId }: { clubId: number }) => {
  const download = useDownloadClubClaimsCsv(clubId)
  const backgroundExport = useClubClaimsExport(clubId)
  const [phase, setPhase] = useState<Phase>({ state: 'idle' })

  // Polling outlives a render, so it has to be stoppable from a cleanup — otherwise navigating away
  // mid-export leaves a timer running against an unmounted component and, worse, saves a file into
  // a page the director has already left.
  const cancelled = useRef(false)
  useEffect(() => {
    cancelled.current = false
    return () => {
      cancelled.current = true
    }
  }, [])

  const runDirectDownload = () => {
    setPhase({ state: 'working', message: 'Building your CSV…' })
    download.mutate(undefined, {
      onSuccess: () => setPhase({ state: 'idle' }),
      onError: (error) =>
        setPhase({
          state: 'failed',
          message: serverErrorMessage(error, 'We could not build that file. Please try again.'),
        }),
    })
  }

  const runBackgroundExport = async () => {
    setPhase({ state: 'working', message: 'Starting your export…' })
    try {
      const runId = await backgroundExport.start()
      const deadline = Date.now() + POLL_TIMEOUT_MS
      setPhase({ state: 'working', message: 'Building your export…' })

      while (!cancelled.current) {
        if (Date.now() > deadline) {
          setPhase({
            state: 'failed',
            message: 'This is taking longer than expected. Your export may still finish — try again shortly.',
          })
          return
        }
        // Waited BEFORE the first poll on purpose: the job has only just been queued, and an
        // immediate poll answers RUNNING every time for no reason other than to say so.
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
        if (cancelled.current) return

        const status = await backgroundExport.poll(runId)
        if (status === 'COMPLETED') {
          // `poll` saved the file the moment the response came back as CSV — there is nothing left
          // to do but say so.
          setPhase({ state: 'idle' })
          return
        }
        if (status === 'FAILED') {
          setPhase({ state: 'failed', message: 'The export failed while it was being built. Please try again.' })
          return
        }
      }
    } catch (error) {
      if (cancelled.current) return
      setPhase({ state: 'failed', message: serverErrorMessage(error, 'We could not start that export.') })
    }
  }

  const busy = phase.state === 'working'

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <CMButton variant="ghost" disabled={busy} onClick={runDirectDownload}>
        Download CSV
      </CMButton>
      <CMButton variant="ghost" disabled={busy} onClick={runBackgroundExport}>
        Build in the background
      </CMButton>

      {phase.state === 'working' && <span className="text-xs text-gray-500">{phase.message}</span>}
      {phase.state === 'failed' && <span className="text-xs text-red-600">{phase.message}</span>}
      {phase.state === 'idle' && (
        <span className="text-xs text-gray-400">Member names are shortened to a surname initial in both files.</span>
      )}
    </div>
  )
}
